import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import { formatApiError } from '../../lib/utils';
import { Eye, EyeSlash, ArrowLeft, CheckCircle } from '@phosphor-icons/react';

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { forgotPassword, verifyOTP, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setStep(2);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyOTP(email, otp);
      setStep(3);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await resetPassword(email, otp, newPassword);
      setStep(4);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="forgot-password-page">
      <div className="w-full max-w-md p-8">
        <div className="bg-white border border-zinc-200 rounded-sm p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-extrabold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope' }}>
              Finadore
            </h1>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2 font-semibold">
              {step === 4 ? 'Password Reset' : 'Reset Password'}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm rounded-sm mb-6" data-testid="forgot-password-error">
              {error}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <p className="text-sm text-zinc-600 text-center">
                Enter your email address and we'll send you an OTP to reset your password.
              </p>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@finadore.com"
                  required
                  className="rounded-sm"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
                data-testid="send-otp-button"
              >
                {loading ? 'Sending...' : 'Send OTP'}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-[#002FA7] hover:underline inline-flex items-center gap-1">
                  <ArrowLeft size={14} /> Back to Login
                </Link>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <p className="text-sm text-zinc-600 text-center">
                Enter the 6-digit OTP sent to <strong>{email}</strong>
              </p>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
                data-testid="verify-otp-button"
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm text-[#002FA7] hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft size={14} /> Change Email
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <p className="text-sm text-zinc-600 text-center">
                Create a new password for your account.
              </p>
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="rounded-sm pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="rounded-sm"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
                data-testid="reset-password-button"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {step === 4 && (
            <div className="text-center space-y-6">
              <CheckCircle size={48} className="mx-auto text-green-500" weight="fill" />
              <p className="text-sm text-zinc-600">
                Your password has been reset successfully. You can now log in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
                data-testid="go-to-login-button"
              >
                Go to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
